import React from 'react';
import { ModalGenericRules, ModalGenericRulesProps } from '../../../../components/ModalGenericRules';
import {
  INFO_GENERIC_RULES_ACTION,
  INFO_GENERIC_RULES_CONDITION,
  INFO_GENERIC_RULES_WHEN
} from '../../../../utils/constants';
import { ResponseGenericModal, useGenericModal } from '../../../../hooks/useGenericModal';
import { ProductItemProps } from '../../../../models/Product';
import { useContextSelector } from 'use-context-selector';
import { PAGES } from '../../../../Router/pages';
import { RouterContext } from '../../../../contexts/RouterContext';
import { PatientProps } from '../../../../contexts/PatientContext';
import { ServiceItemProps } from '../../../../models/Service';
import { TYPE_ROUTE_PRODUCT, useProductRuleActions } from '../../../../hooks/useProductRuleActions';
import { useSelectCoverage } from '../../../../hooks/useSelectCoverage';
import { CartContext } from '../../../../contexts/CartContext';
import { ScheduleContext } from '../../../../contexts/ScheduleContext';
import { typeLocationSchedule } from '../../../../models/Schedule';
import { useSchedulingMount } from '../../../../hooks/useSchedulingMount';
import { Unit } from '../../../../components/ServiceItem';
import { ModuleContext } from '../../../../contexts/ModuleContext';
import { ModalsContext } from '../../../../contexts/ModalsContext';

type GenericModalProps = {
  productList: ProductItemProps[];
  setModalGenericVisible: (value: boolean) => void;
  handleActionButtonModal: (actionButton: string, data: any) => Promise<ResponseGenericModal | void>;
  patientSelected: PatientProps | undefined;
  setGenericModalWhen: (value: React.SetStateAction<string>) => void;
  genericModalCondition: string;
  selectedProduct: ProductItemProps | undefined;
  selectedService: ServiceItemProps | undefined;
  routeProductSelector: ({
    productProp,
    alreadyShowModal
  }: {
    productProp?: ProductItemProps;
    alreadyShowModal?: boolean;
  }) => Promise<void>;
  handleModalProps: (props: { condition?: string | undefined; visible: boolean }) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  genericModal: {
    genericModalCondition: string;
    setGenericModalCondition: React.Dispatch<React.SetStateAction<string>>;
    handleActionButtonModal(actionButton: string, data: any): Promise<ResponseGenericModal | undefined>;
    modalGenericVisible: boolean;
    setModalGenericVisible: React.Dispatch<React.SetStateAction<boolean>>;
    handleModalProps: (props: { condition?: string | undefined; visible: boolean }) => void;
  };
} & ModalGenericRulesProps;

export const GenericModal: React.FC<GenericModalProps> = ({
  productList,
  setModalGenericVisible,
  handleActionButtonModal,
  patientSelected,
  setGenericModalWhen,
  genericModalCondition,
  selectedProduct,
  selectedService,
  routeProductSelector,
  handleModalProps,
  setLoading,
  genericModal,
  ...rest
}) => {
  const { openModal } = useContextSelector(ModalsContext, state => state);
  const { findProduct } = useContextSelector(CartContext, state => state);
  const { goTo } = useContextSelector(RouterContext, state => state);
  const { addUnitDatesAvailable, changeSchedule, selectedItem } = useContextSelector(ScheduleContext, state => state);
  const { selectItemToSchedule, handleSelectProduct } = useContextSelector(ModuleContext, state => state);
  const productRules = selectedProduct?.rules ?? [];

  const { handleSelectedCoverage, loading, selectedUnit, selectedCoverage } = useSelectCoverage({
    handleModalProps: handleModalProps,
    changeSchedule,
    openModal,
    scheduleSelected: selectedItem,
    addUnitDatesAvailable,
    productRules
  });
  const { addGenericModals } = useGenericModal({});
  const useScheduling = useSchedulingMount({ changeSchedule, selectedSchedule: selectedItem });
  const productRuleActions = useProductRuleActions({ goTo, openModal });
  const findProductListService = (productId: string) => {
    return productList.find(product => product.id === productId);
  };

  React.useEffect(() => {
    setLoading(loading);
  }, [loading]);

  React.useEffect(() => {
    if (selectedUnit && selectedCoverage?.care_type === typeLocationSchedule.TELEMEDICINE)
      handleTelemedicineSchedule(selectedUnit);
  }, [selectedUnit, selectedCoverage]);

  function handleTelemedicineSchedule(unit: Unit) {
    useScheduling.addUnit({
      unit: unit,
      localId: unit?.locations?.length ? unit?.locations[0]?.name : ''
    });
    useScheduling.updateSchedule();
    selectItemToSchedule('PRODUCT');
    const productValidated = findProduct(selectedProduct?.id || '');
    if (productValidated) handleSelectProduct(productValidated);
    goTo(PAGES.SERVICE_UNIT.id);
  }

  async function handleGenericModalActionButton(actionButton: string, data: any) {
    if (actionButton === INFO_GENERIC_RULES_ACTION.OPEN_MODAL_CONDITION) {
      genericModal.setGenericModalCondition(data);
    }
    // setGenericModalWhen(INFO_GENERIC_RULES_WHEN.after_product_selection);
    if (actionButton === INFO_GENERIC_RULES_ACTION.CLOSE_MODAL || actionButton === INFO_GENERIC_RULES_ACTION.CLOSE)
      setModalGenericVisible(false);
    if (actionButton === INFO_GENERIC_RULES_ACTION.TO_SERVICE_TYPE) {
      setModalGenericVisible(false);
      if (selectedProduct && patientSelected) {
        const productValidated = findProduct(selectedProduct.id);
        const productIsOnlyTemeledicine = productRuleActions.handleTelemedicineProduct(productValidated);
        if (productIsOnlyTemeledicine?.type === TYPE_ROUTE_PRODUCT.only_telemedicine) {
          if (productIsOnlyTemeledicine.coverage)
            handleSelectedCoverage({
              coverage: productIsOnlyTemeledicine.coverage,
              patientSelected
            });
        }
        if (!productIsOnlyTemeledicine) {
          if (genericModalCondition === INFO_GENERIC_RULES_CONDITION.is_first_appointment) {
            goTo(PAGES.SERVICE_TYPE_PAGE.id);
          } else {
            await routeProductSelector({
              alreadyShowModal: true,
              productProp: selectedProduct
            });
          }
        }
      }
      if (selectedProduct && !patientSelected) {
        await routeProductSelector({
          alreadyShowModal: true,
          productProp: selectedProduct
        });
      }
    }

    if (actionButton === INFO_GENERIC_RULES_ACTION.CHANGE_PRODUCT) {
      setModalGenericVisible(false);
      if (selectedService) {
        const newScheduleProduct = findProductListService(data);
        if (newScheduleProduct && patientSelected) {
          addGenericModals({
            productRules: newScheduleProduct.rules
          });
          await routeProductSelector({
            productProp: newScheduleProduct,
            alreadyShowModal: true
          });
        } else
          await routeProductSelector({
            productProp: newScheduleProduct,
            alreadyShowModal: true
          });
      }
    }
  }

  return <ModalGenericRules {...rest} handleActionButton={handleGenericModalActionButton} />;
};
